import React, { useEffect, useState } from 'react';
import Button from '../../../shared/components/ui/Button';
import Input from '../../../shared/components/ui/Input';
import TextArea from '../../../shared/components/ui/TextArea';
import useCategories from '../../jobs/hooks/useCategories';
import useSkillsByCategory from '../../jobs/hooks/useSkillsByCategory';
import useTowns from '../../jobs/hooks/useTowns';
import TownAutocomplete from '../../../shared/components/inputs/TownAutocomplete';

function PostJobForm({ onSubmit, isSubmitting = false, initialData = {}, submitLabel = 'Post job', onCancel }) {
  const { categories, loading: categoriesLoading } = useCategories();
  const { towns, loading: townsLoading } = useTowns();
  const { skills, loading: skillsLoading, error: skillsError, setCategorySlug } = useSkillsByCategory();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    skillId: '',
    city: '',
    area: '',
    budget: '',
    expiresAt: '',
    requiredPeople: 1,
    workType: 'PART_TIME',
    isNegotiable: true,
    urgency: 'urgent',
    ...initialData,
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!initialData.category || categories.length === 0) return;

    const matchingCategory = categories.find((category) => (
      [category.id, category.slug, category.name].filter(Boolean).some(
        (value) => value.toLowerCase?.() === String(initialData.category).toLowerCase(),
      )
      || (initialData.categoryName && category.name?.toLowerCase() === initialData.categoryName.toLowerCase())
    ));

    if (!matchingCategory) return;

    const categoryValue = matchingCategory.slug || matchingCategory.id;
    setFormData((prev) => ({ ...prev, category: categoryValue }));
    setCategorySlug(categoryValue);
  }, [categories, initialData.category, initialData.categoryName, setCategorySlug]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'category') {
        setCategorySlug(value);
        next.skillId = '';
      }

      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    try {
      const [year, month, day] = formData.expiresAt.split('-');
      const submitData = {
        title: formData.title,
        description: formData.description,
        skillId: formData.skillId,
        city: formData.city,
        area: formData.area,
        budget: Number(formData.budget),
        expiresAt: `${day}.${month}.${year}`,
        isNegotiable: formData.isNegotiable,
        urgency: formData.urgency,
        requiredPeople: Number(formData.requiredPeople) || 1,
        workType: formData.workType,
      };
      await onSubmit(submitData);
    } catch (err) {
      setError(err.message || 'Unable to post job.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 rounded-3xl bg-white p-8 shadow-sm">
      {error && (
        <div className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <Input label="Job title" name="title" placeholder="Need a Plumber in Lusaka" value={formData.title} onChange={handleChange} required />

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Category</label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            disabled={categoriesLoading}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
            required
          >
            <option value="">{categoriesLoading ? 'Loading categories...' : 'Select category'}</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.slug || cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Skill</label>
          <select
            name="skillId"
            value={formData.skillId}
            onChange={handleChange}
            disabled={!formData.category || skillsLoading}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
            required
          >
            <option value="">
              {formData.category ? (skillsLoading ? 'Loading skills...' : 'Select skill') : 'Select category first'}
            </option>
            {skills.map((skill) => (
              <option key={skill.id} value={skill.id}>
                {skill.name}
              </option>
            ))}
          </select>
          {skillsError && <p className="text-xs text-rose-600">Unable to load skills for this category.</p>}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Work type</label>
          <select
            name="workType"
            value={formData.workType}
            onChange={handleChange}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
            required
          >
            <option value="ONE_TIME">One time</option>
            <option value="PART_TIME">Part time</option>
            <option value="FULL_TIME">Full time</option>
            <option value="CONTRACT">Contract</option>
            <option value="SHIFT">Shift</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Required people</label>
          <input
            type="number"
            name="requiredPeople"
            value={formData.requiredPeople}
            onChange={handleChange}
            min="1"
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
            required
          />
        </div>
      </div>

      <TownAutocomplete
        id="post-job-city"
        label="City"
        value={formData.city}
        onChange={(city) => setFormData((prev) => ({ ...prev, city }))}
        towns={towns}
        loading={townsLoading}
        required
        placeholder="Search city or town"
        inputClassName="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
      />

      <Input label="Area" name="area" placeholder="Matero" value={formData.area} onChange={handleChange} required />

      <Input label="Budget" name="budget" type="number" placeholder="150" value={formData.budget} onChange={handleChange} required />

      <Input label="Expires at" name="expiresAt" type="date" value={formData.expiresAt} onChange={handleChange} required />

      <div className="flex items-center space-x-2">
        <input type="checkbox" name="isNegotiable" checked={formData.isNegotiable} onChange={handleChange} />
        <label className="text-sm text-slate-700">Negotiable</label>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">Urgency</label>
        <select
          name="urgency"
          value={formData.urgency}
          onChange={handleChange}
          className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
        >
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>

      <TextArea label="Job description" name="description" placeholder="Fix leaking kitchen pipes and bathroom taps" value={formData.description} onChange={handleChange} rows={6} required />

      <div className="flex flex-wrap gap-3">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : submitLabel}
        </Button>
        {onCancel && <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>}
      </div>
    </form>
  );
}

export default PostJobForm;